import bus from '@/plugins/eventbus'
import Preferences from '@/plugins/preferences'
import { stateGetters, stateSetters, watchGetterAndPersist } from '@/utils/store/store-utils'
import getBrowser from '@/plugins/browser-polyfill'
import { getTopSortOrder } from '@/utils/item-view-utils'
import { buildSiblingParams } from '@/utils/sibling-params'
import { insertMixedResources } from '@/utils/store/mixed-resources'

import CollectionItem, { SHOW_COUNT } from '@/store/models/collectionItems/CollectionItem'
import Collection from '@/store/models/collections/Collection'
import {
  BOOKMARK_SORT_FIELDS,
  ITEM_LAYOUT_STYLE,
  MAX_READ_TIME,
  TAG_MATCH,
  collectionItemFilterDefaults,
  collectionItemsFilterDefaults,
} from '@/utils/constants'
import ResourceTag from '@/store/models/collectionItems/ResourceTag'

const refetchQueue = new Set()

const defaultState = () => ({
  listStyle: ITEM_LAYOUT_STYLE.cards,
  collectionListStyle: ITEM_LAYOUT_STYLE.cards,
  collectionId: null,
  // stats
  itemCount: null,
  duplicateCount: null,
  numberOfPinnedItems: 0,
  maxPinPosition: 0,
  popularTags: null,
  languages: null,
  contributors: [],
  deletedItemCount: null,
  filteredItemCount: null, // INFO: itemCount is the total item count without any filter applied
  importQueueSize: null,
  sumImportQueue: null,
  lastImportSize: null,
  // import tracking
  activeImport: null, // { importId, totalBookmarks, startedAt }
  // pagination
  next: null,
  show: 0,
  // filters
  filters: {},
  filterOpen: false,
  // tier list: the Version picked on each board, { selected, holder } (useTierList)
  pickedTierListVersions: {},
  // cache busting
  cacheVersion: 0,
})

export default {
  name: 'collectionItems',
  namespaced: true,
  state: defaultState(),
  getters: {
    ...stateGetters(defaultState()),
    isCollection(s) {
      return !!s.collectionId
    },
    defaultFilterFn(s, g) {
      return g.isCollection ? collectionItemsFilterDefaults : collectionItemFilterDefaults
    },
    defaultItemsFilterFn() {
      return collectionItemsFilterDefaults
    },
    collectionId(s, g, rs, rootGetters) {
      return s.collectionId ?? rootGetters['auth/collectionId']
    },
    currentRouteCollectionId(s) {
      return s.collectionId
    },

    itemsFilters: (s, g) => {
      const { filters } = s
      return filters[g.collectionId] ?? g.defaultItemsFilterFn()
    },

    filters: (s, g) => {
      const { filters } = s
      return filters[g.collectionId] ?? g.defaultFilterFn()
    },
    sorting: (s, g) => g.filters.sorting,

    // New getters for private collection
    privateCollectionFilters: (s, g, rs, rootGetters) => {
      const privateCollectionId = rootGetters['auth/collectionId']
      return s.filters[privateCollectionId] ?? collectionItemFilterDefaults()
    },
    privateCollectionSorting: (s, g) => {
      const { privateCollectionFilters } = g
      return privateCollectionFilters.sorting
    },
    // Import tracking getter
    activeImport: (s) => s.activeImport,
  },
  mutations: {
    ...stateSetters(defaultState()),
    setFilters(state, { collectionId, filters }) {
      state.filters = {
        ...state.filters,
        [collectionId]: JSON.parse(JSON.stringify(filters)),
      }
    },
    setPickedTierListVersion(state, { board, picked }) {
      state.pickedTierListVersions = {
        ...state.pickedTierListVersions,
        [board]: picked,
      }
    },
    setActiveImport(state, importData) {
      state.activeImport = importData
    },
    clearActiveImport(state) {
      state.activeImport = null
    },
    bumpCacheVersion(state) {
      state.cacheVersion = (state.cacheVersion || 0) + 1
    },
    reset(state) {
      state.collectionId = null
      state.next = null
      state.show = 0
      state.filters = {}
      state.pickedTierListVersions = {}

      state.itemCount = null
      state.importQueueSize = null
      state.sumImportQueue = null
      state.lastImportSize = null
      state.filteredItemCount = null
      state.duplicateCount = null
      state.numberOfPinnedItems = 0
      state.maxPinPosition = 0
      state.languages = null
      state.contributors = []
      state.popularTags = null
      state.activeImport = null
    },
  },
  actions: {
    // ==================== FILTERS ====================
    setupFilters({ commit, rootGetters }, { collectionId, filters }) {
      const isCollection = collectionId !== rootGetters['auth/collectionId']
      commit('setFilters', {
        collectionId,
        filters: filters || (isCollection ? collectionItemsFilterDefaults() : collectionItemFilterDefaults()),
      })
    },

    setFilters({ commit, getters }, val) {
      commit('setNext', null)
      commit('setFilters', { collectionId: getters.collectionId, filters: val })
    },

    setSearch({ state, commit, getters, dispatch }, val) {
      const { collectionId, filters } = getters
      if (!state.filters[collectionId]) {
        dispatch('setupFilters', { collectionId })
      }

      commit('setNext', null)
      commit('setFilters', {
        collectionId,
        filters: { ...filters, search: val },
      })
    },

    setSorting({ state, commit, getters, dispatch }, val) {
      const { collectionId, filters } = getters
      if (!state.filters[collectionId]) {
        dispatch('setupFilters', { collectionId })
      }

      commit('setNext', null)
      commit('setFilters', {
        collectionId,
        filters: { ...filters, sorting: val },
      })
    },

    setFilterTags({ state, commit, getters, dispatch }, v) {
      const { collectionId, filters } = getters
      if (!state.filters[collectionId]) {
        dispatch('setupFilters', { collectionId })
      }

      commit('setNext', null)
      commit('setFilters', {
        collectionId,
        filters: {
          ...filters,
          tags: v,
        },
      })
    },

    addFilterTag({ state, commit, getters, dispatch }, tagId) {
      const { collectionId, filters } = getters
      if (!state.filters[collectionId]) {
        dispatch('setupFilters', { collectionId })
      }

      const currentTags = filters.tags || []
      if (currentTags.includes(tagId)) return

      commit('setNext', null)
      commit('setFilters', {
        collectionId,
        filters: {
          ...filters,
          tags: [...currentTags, tagId],
        },
      })
    },

    setResourceType({ state, commit, getters, dispatch }, resourceType) {
      const { collectionId, filters } = getters
      if (!state.filters[collectionId]) {
        dispatch('setupFilters', { collectionId })
      }

      commit('setNext', null)
      commit('setFilters', {
        collectionId,
        filters: {
          ...filters,
          resourceType,
        },
      })
    },

    async validateFilterTags({ getters, dispatch }) {
      const { filters: { tags }, collectionId } = getters
      if (!tags.length) return

      const validTags = await this.$api.collectionItem.getTagsById(collectionId, {
        params: { ids: tags },
      })

      // insert whichever tags we received into store for future use
      await ResourceTag.insertOrUpdate({ data: validTags })

      // update filters with valid tags
      if (tags.length !== validTags.length) {
        console.warn('Some tags in url filters were not found in collection', tags, validTags)
        dispatch('setFilterTags', validTags.map(i => i._id))
      }
    },

    // ==================== READ ====================
    async getPinnedItems({ getters, dispatch, rootGetters }, searchOpts = {}) {
      const { filters, collectionId, isCollection } = getters
      const search = filters.search?.trim()
      const isFavorite = !isCollection && filters.isFavorite

      const readTime = [
        filters.readTime[0] || false,
        filters.readTime[1] >= MAX_READ_TIME ? false : filters.readTime[1],
      ]

      const { results, next, itemCount } = await this.$api.collectionItem.getPinned(collectionId, {
        params: {
          limit: SHOW_COUNT,
          tags: filters.tags,
          ...(filters.tagMatch === TAG_MATCH.ALL && { tagMatch: TAG_MATCH.ALL }),
          ...(filters.resourceType && { resourceType: filters.resourceType }),
          ...(isFavorite && { isFavorite }),
          ...(search?.length >= 2 && { search }),
          ...(readTime.some(Boolean) && { readTime }),
          ...(filters.languages?.length && { languages: filters.languages }),
          ...(filters.addedBy?.length && { addedBy: filters.addedBy }),
          ...searchOpts,
        },
      })

      try {
        await CollectionItem.insertOrUpdate({
          data: results.map((i, idx) => ({
            ...i,
            sortOrder: idx,
            isPrivate: !isCollection,
          })),
        })
      } catch (error) {
        console.error('Error inserting pinned items', error)
      }

      dispatch('refetchLoadingItems', { collectionId, items: results })

      return { results, next, itemCount }
    },

    async getMixedPinned({ getters }) {
      const { collectionId } = getters
      const { results } = await this.$api.collection.getMixedPinned(collectionId)

      const pinnedItems = results.filter(r => r._pinResourceType === 'CollectionItem')
      const pinnedCollections = results.filter(r => r._pinResourceType !== 'CollectionItem')

      await insertMixedResources(
        { items: pinnedItems, subCollections: pinnedCollections },
        { collectionId },
      )

      return {
        pinnedItemCount: pinnedItems.length,
      }
    },

    async getItems({ getters, dispatch, rootGetters }, searchOpts = {}) {
      const { filters, collectionId, isCollection } = getters

      const readTime = [
        filters.readTime[0] || false,
        filters.readTime[1] >= MAX_READ_TIME ? false : filters.readTime[1],
      ]

      const isFavorite = !isCollection && filters.isFavorite
      const search = filters.search?.trim()

      const { results, next, itemCount } = await this.$api.collectionItem.get(collectionId, {
        params: {
          ...(getters.next && { next: getters.next }),
          limit: SHOW_COUNT,
          sortBy: filters.sorting[0],
          asc: filters.sorting[1],
          tags: filters.tags,
          ...(filters.tagMatch === TAG_MATCH.ALL && { tagMatch: TAG_MATCH.ALL }),
          ...(filters.resourceType && { resourceType: filters.resourceType }),
          ...(isFavorite && { isFavorite }),
          ...(search?.length >= 2 && { search }),
          ...(readTime.some(Boolean) && { readTime }),
          ...(filters.languages.length && { languages: filters.languages }),
          ...(filters.addedBy?.length && { addedBy: filters.addedBy }),
          ...searchOpts,
        },
      })

      try {
        const existingCount = CollectionItem.query().where('ownerId', collectionId).count()

        await CollectionItem.insertOrUpdate({
          data: results.map((i, idx) => ({
            ...i,
            sortOrder: existingCount + idx,
            isPrivate: !isCollection,
          })),
        })
      } catch (error) {
        console.error('Error inserting or updating collection items', error)
      }

      dispatch('refetchLoadingItems', { collectionId, items: results })

      return { results, next, itemCount }
    },

    // Unified trash: global, cross-collection deleted items (deletedBy = me). Items are
    // stored under the view's collectionId so the existing ownerId-scoped list query and
    // cleanup keep working; their real origin lives in `sourceCollection` (info row).
    async getDeleted({ getters }, searchOpts = {}) {
      const { collectionId } = getters

      const { results, next } = await this.$api.collectionItem.getDeletedItems({
        params: {
          ...(getters.next && { next: getters.next }),
          limit: 10,
          ...searchOpts,
        },
      })

      const existingCount = CollectionItem.query().where('ownerId', collectionId).count()

      await CollectionItem.insertOrUpdate({
        data: results.map((i, idx) => ({
          ...i,
          ownerId: collectionId,
          sourceCollection: i.sourceCollection ?? null,
          // Private-collection items have no source context -> no info row.
          isPrivate: !i.sourceCollection,
          sortOrder: existingCount + idx,
        })),
      })

      return { results, next }
    },

    // Unified trash restore (single or bulk). Backend groups by target collection,
    // creates collections as needed, and restores per-group atomically.
    async restoreDeleted(_ctx, { itemIds, forceNewCollectionItemIds = [] }) {
      const result = await this.$api.collectionItem.restoreDeletedItems({ itemIds, forceNewCollectionItemIds })

      const restoredIds = result?.restored ?? []
      if (restoredIds.length) {
        await CollectionItem.delete(record => restoredIds.includes(record.id))
      }

      // Repoint still-deleted siblings whose original (deleted) collection was just
      // (re)created during this restore: their info row should now link to that collection
      // instead of showing "collection deleted" — without refetching the deleted list.
      for (const { restoredFrom, collectionId, name } of result?.restoredCollections ?? []) {
        await CollectionItem.update({
          where: record => record.sourceCollection?.deleted && String(record.sourceCollection.id) === String(restoredFrom),
          data: { sourceCollection: { id: collectionId, name, deleted: false, canRestoreHere: true, parent: null } },
        })
      }

      return result
    },

    async getId({ rootGetters, dispatch }, { collectionId, id, prepend = false }) {
      const item = await this.$api.collectionItem.getId(collectionId, id)

      await CollectionItem.insertOrUpdate({
        data: {
          ...item,
          isPrivate: collectionId === rootGetters['auth/collectionId'],
          ...(prepend && { sortOrder: getTopSortOrder(collectionId) }),
        },
      })

      if (item.isLoading) {
        dispatch('refetchItem', { collectionId, id })
      }

      return item
    },

    // Prev/next siblings for a detail page. Reads filters from state by the given
    // collectionId (not the `filters` getter, which keys off the live `collectionId`
    // that gets reset to null when the list view unmounts) so same-session sort +
    // filters are honored; falls back to defaults on a cold deep-link.
    async getSiblings({ state, rootGetters }, { collectionId, id }) {
      const isCollection = collectionId !== rootGetters['auth/collectionId']
      const filters = state.filters[collectionId] ?? (isCollection ? collectionItemsFilterDefaults() : collectionItemFilterDefaults())

      return this.$api.collectionItem.getSiblings(collectionId, id, {
        params: buildSiblingParams(filters, { isCollection }),
      })
    },

    // Queues every item of a read the api is still scraping (isLoading) up for
    // refetchItem, which must find it in the ORM: dispatch after the insert.
    refetchLoadingItems({ dispatch }, { collectionId, items }) {
      items.filter(i => i.isLoading).forEach(i => dispatch('refetchItem', { collectionId, id: i._id }))
    },

    // NOTE this should be replaced with websocket pushing, but not part of MVP
    async refetchItem({ dispatch, rootGetters }, { collectionId, id, attempt = 0 }) {
      if (refetchQueue.has(id)) {
        // if item is already being refetched, don't try again
        if (!attempt) return
      } else {
        refetchQueue.add(id)
      }

      const item = await this.$api.collectionItem.getId(collectionId, id)

      // update existing item, don't insert
      await CollectionItem.update({
        where: id,
        data: { ...item, isPrivate: collectionId === rootGetters['auth/collectionId'] },
      })

      // successfully refetched, stop trying
      if (!item.isLoading) {
        refetchQueue.delete(id)
        return
      }

      if (attempt < 10) {
        setTimeout(() => {
          dispatch('refetchItem', { collectionId, id, attempt: attempt + 1 })
        }, attempt < 3 ? 3e3 : Math.min(5e3 * (attempt - 2), 15e3))

        return
      }

      console.error(`Failed to refetch item ${id} after ${attempt} attempts`, item)
      refetchQueue.delete(id)
    },

    // ==================== CREATE ====================
    async createBookmark({ getters }, form) {
      const collectionId = form.collectionId ?? getters.collectionId
      const ownerData = await this.$api.collectionItem.createBookmark(collectionId, form.data)

      return ownerData
    },

    async createNote({ getters }, form) {
      const collectionId = form.collectionId ?? getters.collectionId
      const ownerData = await this.$api.collectionItem.createNote(collectionId, form.data)

      return ownerData
    },

    // Upload one image as a file item. Resolves { [collectionId]: id, scanStatus }.
    async createFile({ getters }, { collectionId, file, onUploadProgress }) {
      return this.$api.collectionItem.createFile(collectionId ?? getters.collectionId, file, { onUploadProgress })
    },

    // Scan state of own uploads still in the scan phase: { [id]: 'pending' | 'clean' | 'infected' }
    async getFilesScanStatus({ getters }, { collectionId, ids }) {
      return this.$api.collectionItem.getFilesScanStatus(collectionId ?? getters.collectionId, ids)
    },

    // ==================== STATS ====================
    async getStats({ commit, getters }) {
      const stats = await this.$api.collectionItem.stats(getters.collectionId)
      commit('setDeletedItemCount', stats.deletedItemCount)
      commit('setItemCount', stats.itemCount)
      commit('setImportQueueSize', stats.importQueueSize)
      commit('setSumImportQueue', stats.sumImportQueue)
      commit('setLastImportSize', stats.lastImportSize)
      commit('setDuplicateCount', stats.duplicateCount - (stats.duplicateUrlCount || 0))
      commit('setNumberOfPinnedItems', stats.numberOfPinnedItems ?? 0)
      commit('setMaxPinPosition', stats.maxPinPosition ?? 0)

      const languages = Object.entries(stats.languages || {}).map(([code, count]) => ({ code, count }))
      commit('setLanguages', languages)

      return stats
    },

    // ==================== CONTRIBUTORS ====================
    async getContributors({ commit, getters }) {
      const contributors = await this.$api.collection.contributors(getters.collectionId)
      commit('setContributors', contributors)
      return contributors
    },

    // ==================== TAGS ====================
    async getPopularTags({ commit, getters }) {
      const tags = await this.$api.collectionItem.popularTags(getters.collectionId)

      await ResourceTag.insertOrUpdate({ data: tags })
      commit('setPopularTags', tags.map(tag => ({ id: tag._id, count: tag.count })))

      return tags
    },

    async searchTags({ getters }, options) {
      const collectionId = options.collectionId || getters.collectionId
      const { results, next } = await this.$api.collectionItem.searchTags(collectionId, {
        params: options,
      })
      await ResourceTag.insertOrUpdate({ data: results })

      return { results, next }
    },

    async getTagsById({ getters }, ids) {
      const tags = await this.$api.collectionItem.getTagsById(getters.collectionId, { params: { ids } })
      await ResourceTag.insertOrUpdate({ data: tags })
      return tags
    },

    // ==================== IMPORT / EXPORT ====================
    async import({ commit, getters, dispatch }, file) {
      console.log('[Store] ========== IMPORT ACTION CALLED ==========')
      console.log('[Store] Starting import with file:', file?.name || 'unknown', file)
      console.log('[Store] Collection ID:', getters.collectionId)
      const response = await this.$api.collectionItem.importItems(getters.collectionId, file)

      // Extract data from axios response if needed
      let result = response
      if (response && response.data) {
        result = response.data
      }
      console.log('[Store] Import API response:', result, 'Full response:', response)

      // Store import state in Vuex and emit event for WebSocket tracking
      if (result && result.importId) {
        const importData = {
          importId: result.importId,
          totalBookmarks: result.totalBookmarks || 0,
          startedAt: new Date().toISOString(),
        }
        console.log('[Store] Storing import state in Vuex:', importData)

        // Store import state in Vuex
        commit('setActiveImport', importData)

        // Persist in localStorage so reload can recover toast even if event handlers aren't initialized yet
        try {
          localStorage.setItem('activeImport', JSON.stringify(importData))
          console.log('[Store] Persisted active import to localStorage')
        } catch (error) {
          console.error('[Store] Failed to persist active import to localStorage:', error)
        }

        console.log('[Store] Emitting import:started event with:', {
          importId: result.importId,
          totalBookmarks: result.totalBookmarks || 0,
        })
        bus.emit('import:started', {
          importId: result.importId,
          totalBookmarks: result.totalBookmarks || 0,
        })
      } else if (result && (result.inserted !== undefined || result.failed !== undefined)) {
        // Synchronous import - show completion toast immediately
        console.log('[Store] Synchronous import completed:', result)

        // Create a fake import ID for tracking
        const fakeImportId = `sync-${Date.now()}`
        const totalBookmarks = (result.inserted || 0) + (result.failed || 0)

        // Create import data for Vuex only (sync imports don't need localStorage persistence)
        const importData = {
          importId: fakeImportId,
          totalBookmarks,
          startedAt: new Date().toISOString(),
        }
        // Set in Vuex for consistency (but don't persist sync imports to localStorage)
        commit('setActiveImport', importData)

        // Show progress toast with immediate completion
        console.log('[Store] Emitting import:started for synchronous import')
        bus.emit('import:started', {
          importId: fakeImportId,
          totalBookmarks,
        })

        // Immediately complete it
        setTimeout(() => {
          console.log('[Store] Emitting import:completed for synchronous import')
          bus.emit('import:completed', {
            importId: fakeImportId,
            status: result.failed > 0 ? 'failed' : 'completed',
            totalBookmarks,
            bookmarksProcessed: result.inserted || 0,
            bookmarksFailed: result.failed || 0,
          })
        }, 100)
      } else {
        console.warn('[Store] Unexpected import result format:', result)
      }

      dispatch('auth/getMe', null, { root: true })?.catch(console.error)
      return result // Return the data, not the full response
    },

    async export({ getters }, { format, email = undefined, resourceType = undefined }) {
      return this.$api.collectionItem.exportItems(getters.collectionId, {
        type: format,
        ...(email && { email }),
        ...(resourceType && { resourceType }),
      })
    },
  },
}

bus.$once('app:created', async app => {
  // TODO migrate (sorting is wrong)
  const listStyle = await Preferences.get('collectionItemStyle')
  if (ITEM_LAYOUT_STYLE[listStyle]) {
    app.config.globalProperties.$store.commit('collectionItems/setListStyle', ITEM_LAYOUT_STYLE[listStyle])
  }

  watchGetterAndPersist(app.config.globalProperties.$store, 'collectionItems/listStyle', 'collectionItemStyle')

  // TODO migrate (sorting is wrong)
  const sorting = await Preferences.get('collectionItemSorting')
  const collectionId = await Preferences.get('collectionId')

  if (sorting && collectionId) {
    const [sortField, direction] = sorting

    if (BOOKMARK_SORT_FIELDS[sortField] && typeof direction === 'boolean') {
      app.config.globalProperties.$store.dispatch('collectionItems/setSorting', [sortField, Boolean(direction)])
    }
  }

  watchGetterAndPersist(app.config.globalProperties.$store, 'collectionItems/privateCollectionSorting', 'collectionItemSorting')

  // extension syncing
  if (BUILD_TARGET === 'extension') {
    // NOTE: keep this listener non-async and fire-and-forget. An async listener always
    // returns a Promise, which makes it claim the sendMessage response channel and race
    // (resolving undefined) against the background worker's real responder.
    (await getBrowser()).runtime.onMessage.addListener(({ event, data }) => {
      if (event === 'app:new-item') {
        app.config.globalProperties.$store.dispatch('collectionItems/getId', {
          id: data.itemId,
          collectionId: data.collectionId,
          prepend: true,
        }).catch(console.error)
      }
    })
  }
})
